import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from './layout';
import { Helmet } from 'react-helmet';
import { DidomiSDK } from '@didomi/react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectDidomiConsentAdAccepted,
  selectDidomiConsentCookieAccepted,
  selectDidomiConsentSocialAccepted,
  selectSkipDidomiConsent,
  userDidomiConsentAcceptedOp,
} from '../ducks';
import { settings } from '../settings';
import TagManager from 'react-gtm-module';
import { initializeGA } from '../ga';
import { useNoboScript } from '../scripts';

let scriptsInitialized = false;
let didomiInstance = null;

// for testing purposes only, should not be used outside unittests
export const resetApp = () => {
  scriptsInitialized = false;
  didomiInstance = null;
};

const didomiStatus = () =>
  didomiInstance ? didomiInstance.getUserConsentStatusForAll().purposes : { enabled: [], disabled: []};

export const App = ({ history, debug = false, staticContext }) => {
  const [didomiInitialized, setDidomiInitialized] = useState(false);
  const skipDidomiConsent = useSelector(selectSkipDidomiConsent);
  const didomiConsentCookieAccepted = useSelector(selectDidomiConsentCookieAccepted);
  const didomiConsentAdAccepted = useSelector(selectDidomiConsentAdAccepted);
  const didomiConsentSocialAccepted = useSelector(selectDidomiConsentSocialAccepted);
  const dispatch = useDispatch();
  useNoboScript();

  const onDidomiNoticeHidden = useCallback(() => {
    dispatch(userDidomiConsentAcceptedOp(didomiStatus()));
    setDidomiInitialized(true);
  }, []);

  const onDidomiReady = useCallback(didomi => {
    didomiInstance = didomi;
    if (!didomi.notice.isVisible()) {
      dispatch(userDidomiConsentAcceptedOp(didomiStatus()));
      setDidomiInitialized(true);
    }
  }, []);

  useEffect(() => {
    if (!scriptsInitialized && (didomiInitialized || skipDidomiConsent)) {
      if(didomiConsentCookieAccepted && didomiConsentAdAccepted || skipDidomiConsent) {
        TagManager.initialize({ gtmId: settings.GTM_TRACKING_ID });
      }
      initializeGA(history, { debug }, !(didomiConsentCookieAccepted && didomiConsentAdAccepted));
      scriptsInitialized = true;
    }
  }, [didomiInitialized, skipDidomiConsent, didomiConsentCookieAccepted, didomiConsentAdAccepted]);

  useEffect(() => {
    setTimeout(() => {
      setDidomiInitialized(initialized => {
        if (!didomiInstance) {
          dispatch(userDidomiConsentAcceptedOp(didomiStatus()));
          return true;
        }
        return initialized;
      });
    }, 6000);

  }, []);

  return (
    <>
      <Helmet>
        <title>dumpert.nl</title>
        <meta name="description" content="De plek voor je dagelijkse entertainment.
      Met de leukste filmpjes en foto's voor als je even geen zin hebt in werk, studie of menselijk contact.
      Hoofdsponsor van Nederlandse zolderkamers."/>
        <meta property="og:type" content="website"/>
        <meta property="og:url" content="https://www.dumpert.nl/"/>
        <meta property="og:locale" content="nl_NL"/>
        <meta name="dcterms.rights" content="MediaHuis Nederland B.V." />
        {/* eslint-disable-next-line max-len */}
        <link rel="license" href="https://www.mediahuis.nl/algemene-voorwaarden/gebruikersvoorwaarden-uitgaven/general-terms-of-use-for-mediahuis-publications/" />        
        {didomiConsentSocialAccepted && <meta property="fb:pages" content="332145270132218"/>}
        {didomiConsentSocialAccepted && <meta property="fb:app_id" content="227370684433724"/>}
      </Helmet>
      <Layout staticContext={staticContext}/>
      {!skipDidomiConsent && <DidomiSDK
        apiKey={settings.DIDOMI_API_KEY}
        onNoticeHidden={onDidomiNoticeHidden}
        onReady={onDidomiReady}
        onConsentChanged={() => window.location.reload()}
      />}
    </>
  );
};

App.Routes = Layout.Routes;
